import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import type { User } from "@/types/auth";

/**
 * Formulario para crear/editar usuarios
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Todos los componentes con size="small"
 * - Validación con class-validator en backend
 * - Máximo 500 líneas
 */

interface UserFormData {
  email: string;
  nombre: string;
  apellido: string;
  telefono: string;
}

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (userData: UserFormData) => Promise<void>;
  user?: User | null; // null para crear, User para editar
  loading?: boolean;
}

export function UserForm({
  open,
  onClose,
  onSave,
  user,
  loading = false,
}: UserFormProps) {
  const [formData, setFormData] = useState<UserFormData>({
    email: "",
    nombre: "",
    apellido: "",
    telefono: "",
  });
  const [errors, setErrors] = useState<Partial<UserFormData>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = Boolean(user);

  // Cargar datos del usuario cuando se abre el modal para edición
  useEffect(() => {
    if (open) {
      if (isEditing && user) {
        setFormData({
          email: user.email,
          nombre: user.nombre || "",
          apellido: user.apellido || "",
          telefono: user.telefono || "",
        });
      } else {
        // Resetear formulario para nuevo usuario
        setFormData({
          email: "",
          nombre: "",
          apellido: "",
          telefono: "",
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [open, isEditing, user]);

  const handleInputChange =
    (field: keyof UserFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Limpiar error del campo cuando el usuario empiece a escribir
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Partial<UserFormData> = {};

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Formato de email inválido";
    }

    // Validar nombre
    if (!formData.nombre.trim()) {
      newErrors.nombre = "El nombre es requerido";
    } else if (formData.nombre.trim().length < 2) {
      newErrors.nombre = "El nombre debe tener al menos 2 caracteres";
    }

    // Validar apellido
    if (!formData.apellido.trim()) {
      newErrors.apellido = "El apellido es requerido";
    } else if (formData.apellido.trim().length < 2) {
      newErrors.apellido = "El apellido debe tener al menos 2 caracteres";
    }

    // Validar teléfono (opcional, pero si se ingresa debe ser válido)
    if (
      formData.telefono.trim() &&
      !/^\+?[\d\s\-\(\)]{7,15}$/.test(formData.telefono.trim())
    ) {
      newErrors.telefono = "Formato de teléfono inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Preparar datos para envío
      const dataToSend: UserFormData = {
        email: formData.email.trim(),
        nombre: formData.nombre.trim(),
        apellido: formData.apellido.trim(),
        telefono: formData.telefono.trim() || "",
      };

      await onSave(dataToSend);
      handleClose();
    } catch (error) {
      console.error("Error guardando usuario:", error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Error desconocido al guardar el usuario",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !isSubmitting) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle>
        {isEditing ? "Editar Usuario" : "Crear Nuevo Usuario"}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {isEditing
            ? "Modifica los datos del usuario existente"
            : "Ingresa los datos del nuevo usuario"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Error de envío */}
          {submitError && <Alert severity="error">{submitError}</Alert>}

          {/* Campo Email */}
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange("email")}
            onKeyPress={handleKeyPress}
            error={Boolean(errors.email)}
            helperText={errors.email}
            required
            size="small"
            fullWidth
            disabled={isSubmitting || loading}
            placeholder="ejemplo@email.com"
          />

          {/* Campo Nombre */}
          <TextField
            label="Nombre"
            value={formData.nombre}
            onChange={handleInputChange("nombre")}
            onKeyPress={handleKeyPress}
            error={Boolean(errors.nombre)}
            helperText={errors.nombre}
            required
            size="small"
            fullWidth
            disabled={isSubmitting || loading}
            placeholder="Nombre del usuario"
          />

          {/* Campo Apellido */}
          <TextField
            label="Apellido"
            value={formData.apellido}
            onChange={handleInputChange("apellido")}
            onKeyPress={handleKeyPress}
            error={Boolean(errors.apellido)}
            helperText={errors.apellido}
            required
            size="small"
            fullWidth
            disabled={isSubmitting || loading}
            placeholder="Apellido del usuario"
          />

          {/* Campo Teléfono */}
          <TextField
            label="Teléfono"
            type="tel"
            value={formData.telefono}
            onChange={handleInputChange("telefono")}
            onKeyPress={handleKeyPress}
            error={Boolean(errors.telefono)}
            helperText={errors.telefono || "Opcional"}
            size="small"
            fullWidth
            disabled={isSubmitting || loading}
            placeholder="+52 55 1234 5678"
          />

          {/* Información adicional para edición */}
          {isEditing && user && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Usuario creado:</strong>{" "}
                {new Date(
                  user.activo ? Date.now() : Date.now(),
                ).toLocaleDateString("es-ES")}
              </Typography>
              <Typography variant="body2">
                <strong>Estado:</strong> {user.activo ? "Activo" : "Inactivo"}
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          startIcon={<CancelIcon />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || loading}
          variant="contained"
          size="small"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {isSubmitting ? "Guardando..." : isEditing ? "Actualizar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
